// ── Section config types ──

export interface HeaderSectionConfig {
	showLogo: boolean;
	logoUrl?: string;
	nameStyle: "bold" | "large" | "uppercase";
	anonymous: boolean;
}

export interface ContactSectionConfig {
	showEmail: boolean;
	showPhone: boolean;
	showLocation: boolean;
	showLinkedin: boolean;
}

export interface SummarySectionConfig {
	source: "ai" | "custom";
	customText?: string;
}

export interface ExperienceSectionConfig {
	showIndustry: boolean;
	showDateRange: boolean;
}

export interface SkillsSectionConfig {
	layout: "list" | "badges";
}

interface EducationSectionConfig {
	// Stub for future
}

export interface CustomSectionConfig {
	title: string;
	content: string;
}

export type SectionConfigMap = {
	header: HeaderSectionConfig;
	contact: ContactSectionConfig;
	summary: SummarySectionConfig;
	experience: ExperienceSectionConfig;
	skills: SkillsSectionConfig;
	education: EducationSectionConfig;
	custom: CustomSectionConfig;
};

export type SectionKey = keyof SectionConfigMap;

export interface CvSectionBlock<K extends SectionKey = SectionKey> {
	key: K;
	enabled: boolean;
	order: number;
	config: SectionConfigMap[K];
}

// ── Defaults ──

export const DEFAULT_SECTION_BLOCKS: CvSectionBlock[] = [
	{ key: "header", enabled: true, order: 0, config: { showLogo: false, nameStyle: "bold", anonymous: false } },
	{ key: "contact", enabled: true, order: 1, config: { showEmail: true, showPhone: true, showLocation: true, showLinkedin: false } },
	{ key: "summary", enabled: true, order: 2, config: { source: "ai", customText: "" } },
	{ key: "experience", enabled: true, order: 3, config: { showIndustry: true, showDateRange: true } },
	{ key: "skills", enabled: true, order: 4, config: { layout: "badges" } },
	{ key: "education", enabled: false, order: 5, config: {} },
	{ key: "custom", enabled: false, order: 6, config: { title: "", content: "" } },
];

export const CV_SECTION_META: Record<SectionKey, { label: string; icon: string }> = {
	header: { label: "Header & Branding", icon: "LayoutTemplate" },
	contact: { label: "Contact Information", icon: "Contact" },
	summary: { label: "Professional Summary", icon: "FileText" },
	experience: { label: "Work Experience", icon: "Briefcase" },
	skills: { label: "Skills", icon: "Wrench" },
	education: { label: "Education", icon: "GraduationCap" },
	custom: { label: "Custom Section", icon: "PenLine" },
};

// ── Helpers ──

export function isLegacySections(sections: unknown): sections is string[] {
	return Array.isArray(sections) && sections.length > 0 && typeof sections[0] === "string";
}

// ── Color scheme ──

export interface CvColorScheme {
	key: string;
	label: string;
	primary: string;
	secondary: string;
	background: string;
	text: string;
}

export interface CustomColors {
	primary: string;
	secondary: string;
	background: string;
	text: string;
}

export const CV_COLOR_SCHEMES: CvColorScheme[] = [
	{ key: "classic", label: "Classic Blue", primary: "#2563eb", secondary: "#64748b", background: "#f8fafc", text: "#1e293b" },
	{ key: "slate", label: "Professional Slate", primary: "#334155", secondary: "#64748b", background: "#f1f5f9", text: "#1e293b" },
	{ key: "emerald", label: "Modern Green", primary: "#059669", secondary: "#6b7280", background: "#f0fdf4", text: "#1e293b" },
	{ key: "violet", label: "Creative Violet", primary: "#7c3aed", secondary: "#6b7280", background: "#f5f3ff", text: "#1e293b" },
	{ key: "rose", label: "Warm Rose", primary: "#e11d48", secondary: "#6b7280", background: "#fff1f2", text: "#1e293b" },
	{ key: "amber", label: "Bold Amber", primary: "#d97706", secondary: "#6b7280", background: "#fffbeb", text: "#1e293b" },
	{ key: "neutral", label: "Minimal B&W", primary: "#171717", secondary: "#737373", background: "#fafafa", text: "#171717" },
];

export function getColorScheme(key: string, customColors?: CustomColors): CvColorScheme {
	if (key === "custom" && customColors)
		return { key: "custom", label: "Custom", ...customColors };
	return CV_COLOR_SCHEMES.find(s => s.key === key) || CV_COLOR_SCHEMES[0];
}

// ── Layouts ──

export type CvLayout = "standard" | "sidebar" | "compact";

export const CV_LAYOUTS: { key: CvLayout; label: string; description: string }[] = [
	{ key: "standard", label: "Standard", description: "Single column, full width" },
	{ key: "sidebar", label: "Sidebar", description: "Two columns with sidebar" },
	{ key: "compact", label: "Compact", description: "Dense single column" },
];

// ── Template types ──

export interface CvTemplate {
	id: string;
	name: string;
	slug?: string;
	description?: string;
	content: string;
	header_html?: string;
	footer_html?: string;
	sections: CvSectionBlock[] | string[];
	styles?: string;
	color_scheme?: string;
	custom_colors?: CustomColors;
	layout?: CvLayout;
	organization_uuid: string;
	created_by: string;
	created_at: string;
	updated_at: string;
	is_default: boolean;
}

export interface CreateCvTemplateData {
	name: string;
	description?: string;
	content?: string;
	header_html?: string;
	footer_html?: string;
	sections?: CvSectionBlock[];
	styles?: string;
	color_scheme?: string;
	custom_colors?: CustomColors;
	layout?: CvLayout;
}

export interface UpdateCvTemplateData {
	name?: string;
	description?: string;
	content?: string;
	header_html?: string;
	footer_html?: string;
	sections?: CvSectionBlock[];
	styles?: string;
	color_scheme?: string;
	custom_colors?: CustomColors;
	layout?: CvLayout;
	is_default?: boolean;
}

// ── Legacy constants (kept for backward compat) ──
